/**
 * The NAGGING framework's own reminders — the frontend seam.
 *
 * Not to be confused with its `useEntityReminders` neighbour, which is the user-scheduled
 * one-shot "Remind me" feature (`docs/platform/entity-reminders-design.md`). These are the
 * condition-driven rules — "you have not spoken to Ada for 214 days", "this invoice is
 * overdue" — that `docs/platform/nagging-reminders-design.md` specifies. They share the
 * bell and nothing else.
 *
 * `GET /active` is the RECONCILIATION seam and the whole reason this file exists. A bell
 * row is an inbox record persisted at EMIT time, so its payload describes the nag as it was
 * when it was sent — after a reader snoozes one and reopens the popover, the row still
 * says what it said before they acted. So the strip never trusts the notification for
 * anything but identity: it looks the state id up here, and a state id this endpoint does
 * NOT return has been resolved, dismissed or expired since, which is exactly what the strip
 * must say instead of offering to snooze it again.
 *
 * A thin naming + invalidation layer over the ORVAL-generated hooks, like its neighbour:
 * both sides come from one OpenAPI document, so it cannot drift the way a hand-rolled
 * client can (`.claude/rules/rules-frontend.md` § hand-rolled API calls).
 */
import { useQueryClient } from '@tanstack/react-query';

import {
  getGetActiveRemindersApiV1RemindersActiveGetQueryKey,
  useDismissReminderApiV1RemindersReminderIdDismissPost,
  useGetActiveRemindersApiV1RemindersActiveGet,
  useSnoozeReminderApiV1RemindersReminderIdSnoozePost,
} from '@/generated/api/reminders/reminders';
import type { ActiveReminderResponse } from '@/generated/api/models/activeReminderResponse';

export type { ActiveReminderResponse };

/**
 * The caller's live nags, indexed by `reminder_states.id`.
 *
 * Fetched only while the bell is open — a nag's state changes when the 5-minute sweep runs
 * or when the reader acts, neither of which is worth a request on every page the app bar
 * renders on.
 */
export function useActiveReminders({ enabled = true }: { enabled?: boolean } = {}) {
  // `undefined` is the PARAMS slot, not padding: the route gained a Shape B
  // `limit` backstop (WI c2d32e29), so the generated hook now takes params first and
  // the query options second. Passing no params is what asks for the complete set —
  // `resolve_limit(None)` is MAX_LIST_LIMIT, never a page size.
  return useGetActiveRemindersApiV1RemindersActiveGet(undefined, { query: { enabled } });
}

/** Index a fetched list by state id, so a row can look itself up in O(1). */
export function indexActiveReminders(
  reminders: ActiveReminderResponse[] | undefined,
): ReadonlyMap<string, ActiveReminderResponse> {
  return new Map((reminders ?? []).map((reminder) => [reminder.id, reminder]));
}

/**
 * Invalidate the active list after an action.
 *
 * Only `/active` — a snooze or a dismiss changes the NAG, not the inbox row that announced
 * it, and there is no delete endpoint for a notification. That is deliberate: the row stays
 * as the historical record that the reader was told, and the strip re-derives what can
 * still be done about it from the refetched state.
 */
function useInvalidateActiveReminders() {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      queryKey: getGetActiveRemindersApiV1RemindersActiveGetQueryKey(),
    });
}

/**
 * Silence one nag until a chosen instant.
 *
 * The instant, not a duration: the presets are wall clock in the reader's own timezone
 * ("next Monday at 9am"), and converting one to minutes on the client re-anchors it on the
 * browser's clock. Errors are NOT handled here — every mutation runs through React Query,
 * and the global `MutationCache` turns a failure into a translated toast, so a local catch
 * would double-toast.
 */
export function useSnoozeReminder() {
  const invalidate = useInvalidateActiveReminders();
  return useSnoozeReminderApiV1RemindersReminderIdSnoozePost({
    mutation: { onSuccess: invalidate },
  });
}

/** End this nag episode for the caller alone. */
export function useDismissReminder() {
  const invalidate = useInvalidateActiveReminders();
  return useDismissReminderApiV1RemindersReminderIdDismissPost({
    mutation: { onSuccess: invalidate },
  });
}
